// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import React from "react";
import Dropzone from "react-dropzone";

import {
  passwordRequirements,
  validPasswordRegex,
} from "src/constants/password";
import ButtonLoader from "src/components/ButtonLoader";
import Loader from "src/components/Loader";
import {
  GreyInfoText,
  ButtonContainer,
  SetupInputAndLabelWrapperGrid,
  SetupFormHeader,
  ProfileSetupGridProvider,
  ImageUploadInput,
  ImagePreview,
  ProfileSetupForm,
} from "src/styles/signup";
import Icon from "src/icons";
import {
  SuccessMessageContainer,
  CircleCheckContainer,
} from "src/components/filter/Profile/styles";
import { FormLabel, ErrorText } from "src/styles/auth";
import { Input, Text } from "@unifize/sarah";
import * as colors from "src/styles/constants/colors";

type Props = {
  joiningOrg: boolean,
  displayName: string,
  password: string,
  imageUrl: ?string,
  profilePhotoError: Object,
  joinUserError: Object,
  onDrop: Function,
  handleChange: Function,
  removeFile: Function,
  handleSubmit: Function,
};

const ProfileSetup = ({
  displayName,
  password,
  imageUrl,
  joinUserError,
  profilePhotoError,
  onDrop,
  joiningOrg,
  handleChange,
  removeFile,
  handleSubmit,
}: Props) => {
  if (joinUserError || profilePhotoError) {
    return <Loader>{i18n.t(k.SETTING_UP_YOUR_PROFILE)}</Loader>;
  }

  const isValidPassword = validPasswordRegex.test(password);

  return (
    <ProfileSetupForm onSubmit={handleSubmit}>
      <SetupFormHeader>
        {i18n.t(k.PLEASE_COMPLETE_YOUR_PROFILE_B)}
      </SetupFormHeader>
      <ProfileSetupGridProvider>
        <SetupInputAndLabelWrapperGrid>
          <FormLabel>
            <GreyInfoText>{i18n.t(k.FULL_NAME)}</GreyInfoText>
          </FormLabel>
          <Input
            autoFocus
            type="text"
            value={displayName}
            placeholder="Enter your full name"
            name="displayName"
            onChange={handleChange}
            required
            autoComplete="off"
          />
        </SetupInputAndLabelWrapperGrid>
        <SetupInputAndLabelWrapperGrid>
          <FormLabel>
            <GreyInfoText>{i18n.t(k.CREATE_PASSWORD)}</GreyInfoText>
          </FormLabel>
          <Input
            type="password"
            value={password}
            placeholder="Create Password"
            name="password"
            onChange={handleChange}
            required
            autoComplete="new-password"
            error={
              !isValidPassword && password.length > 0
                ? i18n.t(passwordRequirements)
                : null
            }
          />
          {isValidPassword ? (
            <SuccessMessageContainer>
              <Text variant="caption" color={colors.greenDark}>
                {i18n.t(passwordRequirements)}
              </Text>
              <CircleCheckContainer>
                <Icon type="circleCheck" />
              </CircleCheckContainer>
            </SuccessMessageContainer>
          ) : null}
        </SetupInputAndLabelWrapperGrid>
        {imageUrl ? (
          <ImagePreview>
            <img src={imageUrl} alt="profile pic" />
            <button type="button" onClick={() => removeFile([])}>
              {i18n.t(k.X3)}
            </button>
          </ImagePreview>
        ) : (
          <Dropzone
            accept="image/*"
            onDrop={onDrop}
            maxSize={2726297}
            multiple={false}
          >
            {({ getRootProps, getInputProps }) => (
              <ImageUploadInput {...getRootProps()}>
                <input {...getInputProps()} />
                {i18n.t(k.ADD_PROFILE_PHOTO_OPTIONAL)}
              </ImageUploadInput>
            )}
          </Dropzone>
        )}
      </ProfileSetupGridProvider>
      {profilePhotoError && <ErrorText>{profilePhotoError.message}</ErrorText>}
      <ButtonContainer>
        <ButtonLoader
          type="submit"
          isLoading={joiningOrg}
          disabled={!isValidPassword || !displayName}
        >
          {i18n.t(k.JOIN)}
        </ButtonLoader>
      </ButtonContainer>
    </ProfileSetupForm>
  );
};

export default ProfileSetup;
